import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getSettings } from '../services/api';
import { Menu, X, Phone, Mail, Clock, Instagram, Facebook, Music2, ExternalLink } from 'lucide-react';
import SpotifyPlayer from './SpotifyPlayer';
import ChatWidget from './ChatWidget';
import MobileCountdownBar from './MobileCountdownBar';
import SponsorLogos from './SponsorLogos';

const allNavLinks = [
  { name: "HOME", path: "/" },
  { name: "GLOSSARIO", path: "/glossario" },
  { name: "BIOGRAFIE", path: "/biografie" },
  { name: "FREESTYLER ISCRITTI", path: "/freestyler" },
  { name: "CYPHER D'ITALIA", path: "/cypher-italia" },
  { name: "ALTRI FORMAT", path: "/altri-format" },
  { name: "Spotify PLAYLIST", path: "/spotify" },
  { name: "SHOP", path: "/shop" },
  { name: "Wall OF FAME", path: "/gallery" },
  { name: "SPONSOR", path: "/sponsor-clienti" },
  { name: "TP STAFF", path: "/tp-staff" },
];

const NEW_BANNER = "https://static.wixstatic.com/media/e93d0f_99957b2ff0c6432799f0abf8d966c189~mv2.jpg/v1/fill/w_2154,h_474,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/banner%20nuovo%20tp%20(1).jpg";

const Layout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const [showContactButton, setShowContactButton] = useState(() => {
    const saved = localStorage.getItem('showContactButton');
    return saved !== 'false';
  });
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    getSettings().then(setSettings).catch(() => {});
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const handleCloseContactButton = (e) => {
    e.stopPropagation();
    setShowContactButton(false);
    localStorage.setItem('showContactButton', 'false');
  };

  const handleContactClick = () => {
    navigate('/contatti');
  };

  const s = settings || {};

  return (
    <div className="min-h-screen flex flex-col pb-[140px]" style={{ backgroundColor: 'var(--tp-black)' }}>
      {/* Hero Banner - Full width image (branding already embedded) */}
      <div className="relative w-full overflow-hidden">
        <img
          src={s.hero_image || NEW_BANNER}
          alt="Tecniche Perfette Banner"
          className="w-full h-auto object-contain"
          style={{ maxHeight: '280px', width: '100%', objectFit: 'cover', objectPosition: 'center' }}
        />
        <div className="absolute bottom-0 left-0 w-1/3 h-1 sm:h-1.5 md:h-2" style={{ backgroundColor: 'var(--tp-red)' }} />

        {/* Social icons in alto a sinistra del banner */}
        <div className="absolute top-3 left-3 sm:top-4 sm:left-4 flex items-center gap-2 z-10">
          <a
            href="https://www.instagram.com/tecnicheperfette"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="banner-social-instagram"
            aria-label="Instagram @tecnicheperfette"
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-black/60 hover:bg-red-600 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white transition-all duration-200 hover:scale-110 shadow-lg"
          >
            <Instagram size={18} />
          </a>
          <a
            href="https://www.facebook.com/tecnicheperfette"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="banner-social-facebook"
            aria-label="Facebook @tecnicheperfette"
            className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-black/60 hover:bg-red-600 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white transition-all duration-200 hover:scale-110 shadow-lg"
          >
            <Facebook size={18} />
          </a>
        </div>
        
        {/* Pulsante Fluttuante Contatti */}
        {showContactButton && (
          <div className="absolute bottom-4 right-4 sm:bottom-6 sm:right-6">
            <div className="relative group">
              <button
                onClick={handleContactClick}
                className="flex items-center gap-2 sm:gap-3 px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg font-bold text-white shadow-2xl hover:shadow-red-500/50 transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: 'var(--tp-red)', fontFamily: 'Oswald, sans-serif' }}
              >
                <Mail size={18} className="sm:w-5 sm:h-5" />
                <span className="text-sm sm:text-base">CONTATTI</span>
              </button>
              <button
                onClick={handleCloseContactButton}
                className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gray-900 text-white hover:bg-red-600 flex items-center justify-center shadow-lg transition-colors duration-200"
                aria-label="Chiudi"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Bar — all items inline */}
      <nav className="sticky top-0 z-50 bg-white shadow-lg">
        {/* Desktop / Tablet Nav — visible from sm (640px) and up */}
        <div className="hidden sm:flex items-center justify-center flex-wrap">
          {allNavLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`px-1.5 sm:px-2 md:px-2.5 lg:px-4 py-2 sm:py-2.5 md:py-3 text-[9px] sm:text-[10px] md:text-xs lg:text-sm font-medium tracking-wide transition-colors duration-200 border-b-2 whitespace-nowrap ${
                location.pathname === link.path
                  ? 'text-black border-red-600 bg-gray-50'
                  : 'text-gray-700 border-transparent hover:text-black hover:border-red-400 hover:bg-gray-50'
              }`}
              style={{ fontFamily: 'Oswald, sans-serif' }}
            >
              {link.name}
            </Link>
          ))}
        </div>

        {/* Mobile Nav — visible only below sm (640px) */}
        <div className="sm:hidden flex items-center justify-between px-4 py-3">
          <Link to="/" className="text-black font-bold text-lg" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            TECNICHE PERFETTE
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-gray-700 p-2 hover:bg-gray-100 rounded"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu Dropdown */}
        {mobileMenuOpen && (
          <div className="sm:hidden bg-white border-t max-h-[70vh] overflow-y-auto">
            {allNavLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`block px-4 py-3 text-sm font-medium border-b border-gray-100 ${
                  location.pathname === link.path
                    ? 'text-red-600 bg-red-50'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {link.name}
              </Link>
            ))}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer style={{ backgroundColor: 'var(--tp-dark)' }} className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 py-8 sm:py-12">
          {/* Sponsor Section - Moved to top */}
          <div className="mb-10 sm:mb-12 pb-8 sm:pb-10 border-b border-gray-800">
            <h3 className="text-white font-bold mb-6 sm:mb-8 text-center tracking-widest text-lg sm:text-xl" style={{ fontFamily: 'Oswald, sans-serif' }}>
              SUPPORT & SPONSOR
            </h3>
            <div className="flex justify-center items-center px-4">
              <SponsorLogos />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-white font-bold mb-4 tracking-wider text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                CONTATTACI
              </h4>
              <div className="space-y-3">
                <div className="flex items-center gap-3 text-gray-400">
                  <Phone size={16} className="text-red-500 flex-shrink-0" />
                  <span className="text-sm">Segreteria {s.phone || "+39 011 2341 6793"}</span>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <Mail size={16} className="text-red-500 flex-shrink-0" />
                  <a href={`mailto:${s.email || "info@tecnicheperfette.com"}`} className="text-sm hover:text-white transition-colors break-all">
                    {s.email || "info@tecnicheperfette.com"}
                  </a>
                </div>
                <div className="flex items-center gap-3 text-gray-400">
                  <Clock size={16} className="text-red-500 flex-shrink-0" />
                  <span className="text-sm">{s.hours || "DALLE 9 ALLE 18"}</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-white font-bold mb-4 tracking-wider text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                SEGUICI
              </h4>
              <div className="flex gap-4">
                <a href={s.instagram || "#"} target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-800 hover:bg-red-600 transition-colors duration-300">
                  <Instagram size={18} />
                </a>
                <a href={s.tiktok || "#"} target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-800 hover:bg-red-600 transition-colors duration-300">
                  <Music2 size={18} />
                </a>
                <a href={s.spotify || "#"} target="_blank" rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full flex items-center justify-center bg-gray-800 hover:bg-green-600 transition-colors duration-300">
                  <ExternalLink size={18} />
                </a>
              </div>
            </div>

            <div className="sm:col-span-2 md:col-span-1">
              <h4 className="text-white font-bold mb-4 tracking-wider text-sm" style={{ fontFamily: 'Oswald, sans-serif' }}>
                TECNICHE PERFETTE
              </h4>
              <p className="text-gray-400 text-sm leading-relaxed">
                {s.description || "Il torneo Nazionale di improvvisazione in rima - Freestyle. Nato nel 2003."}
              </p>
            </div>
          </div>

          {/* Spotify Player */}
          <SpotifyPlayer />

          <div className="mt-8 sm:mt-10 pt-6 border-t border-gray-800 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-gray-500 text-xs">
            <span>&copy; 2003 - {new Date().getFullYear()} MAS NETWORK. Tutti i diritti riservati.</span>
            <span className="hidden sm:inline">|</span>
            <Link to="/privacy" className="text-gray-500 hover:text-red-400 transition-colors underline">Privacy & Cookie Policy</Link>
            <span className="hidden sm:inline">|</span>
            <Link to="/admin" className="text-gray-600 hover:text-red-400 transition-colors">Admin</Link>
          </div>
        </div>
      </footer>

      {/* Live Chat Widget */}
      <ChatWidget />
      <MobileCountdownBar />
    </div>
  );
};

export default Layout;
